import React, { useState, useEffect, useRef } from 'react';
import Container from '../../components/Container';
import useTooltipLogic from './useTooltipLogic';
import { scrollAppIntoView } from '../../utils/pageScroll';
import '../../theme/redesign/optimal.css';

const PATH_TO_BACKEND = process.env.REACT_APP_PATH_TO_BACKEND;

function Optimal({ products, addressAndCoordinates, comparisons, paymentMethod, setSelectedOffer, offerRef, onOptimalRouteChange, selfPickUp, selectedOffer }) {
  const [optimalRoute, setOptimalRoute] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { tooltip, handleMouseEnter, handleMouseLeave, tableContainerRef, tooltipRef } = useTooltipLogic();

  const prevProductsRef = useRef(products);
  const prevPaymentMethodRef = useRef(paymentMethod); // Добавляем ref для отслеживания paymentMethod
  const debounceTimeoutRef = useRef(null);
  // Маршрут, по которому уже собран расчёт заказа — см. эффект пересборки ниже.
  const appliedOptimalRouteRef = useRef(null);

  const getRegionFromCoordinates = async (coordinates) => {
    const regions = {
      'СПБ': { lat: 59.9343, lon: 30.3351 },
      'МСК': { lat: 55.7558, lon: 37.6173 },
      'КРД': { lat: 45.0355, lon: 38.9753 },
    };

    const calculateDistance = (lat1, lon1, lat2, lon2) => {
      const R = 6371;
      const dLat = (lat2 - lat1) * Math.PI / 180;
      const dLon = (lon2 - lon1) * Math.PI / 180;
      const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
                Math.sin(dLon/2) * Math.sin(dLon/2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
      return R * c;
    };

    let closestRegion = '';
    let minDistance = Infinity;

    for (const [region, coords] of Object.entries(regions)) {
      const distance = calculateDistance(
        coordinates.latitude, coordinates.longitude,
        coords.lat, coords.lon
      );
      if (distance < minDistance) {
        minDistance = distance;
        closestRegion = region;
      }
    }

    return minDistance < 100 ? closestRegion : '';
  };

  const areProductsEqual = (prev, next) => {
    if (prev.length !== next.length) return false;
    return prev.every((prevProduct, index) => {
      const nextProduct = next[index];
      return (
        prevProduct.name === nextProduct.name &&
        prevProduct.quantity === nextProduct.quantity &&
        prevProduct.category === nextProduct.category &&
        prevProduct.weight === nextProduct.weight &&
        prevProduct.volume === nextProduct.volume
      );
    });
  };

  useEffect(() => {
    if (selfPickUp) {
      if (optimalRoute) {
        setOptimalRoute(null);
        if (onOptimalRouteChange) onOptimalRouteChange(null);
      }
      return;
    }

    // Игнорируем временные изменения (с флагом _isTemporary)
    const hasTemporaryChanges = products.some(product => product._isTemporary);
    if (hasTemporaryChanges) {
      return;
    }

    prevProductsRef.current = products;
    prevPaymentMethodRef.current = paymentMethod;

    const hasObschestroy = products.some(p => p.category === 'Общестрой' && p.name && !isNaN(parseInt(p.quantity)));

    if (!hasObschestroy) {
      if (optimalRoute) {
        setOptimalRoute(null);
        if (onOptimalRouteChange) onOptimalRouteChange(null);
      }
      return;
    }
        

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      fetchOptimalRoute();
    }, 500);

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [products, onOptimalRouteChange, selfPickUp, addressAndCoordinates.coordinates.latitude, addressAndCoordinates.coordinates.longitude, paymentMethod, addressAndCoordinates.address]); // Добавляем paymentMethod в зависимости

  const fetchOptimalRoute = async () => {
    setLoading(true);
    setError(null);

    try {
      const requiredProducts = products
        .filter(p => p.name && p.category === 'Общестрой' && !isNaN(p.quantity))
        .map(p => ({
          name: p.name,
          quantity: !p.quantity ? 0 : parseInt(p.quantity),
          weight: parseFloat(p.weight) || 0,
          volume: parseFloat(p.volume) || 0,
          category: p.category,
          bitrixId: p.bitrixId || "",
        }));

      if (!requiredProducts.length) {
        setOptimalRoute(null);
        setLoading(false);
        if (onOptimalRouteChange) onOptimalRouteChange(null);
        return;
      }

      const region = await getRegionFromCoordinates(addressAndCoordinates.coordinates);

      console.log('Отправляемые данные:', { products: requiredProducts, region, clientCoordinates: addressAndCoordinates.coordinates, paymentMethod });

      const response = await fetch(`${PATH_TO_BACKEND}/api/optimal-route`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          products: requiredProducts,
          region,
          clientCoordinates: addressAndCoordinates.coordinates,
          paymentMethod: paymentMethod
        })
      });

      console.log('Статус ответа:', response.status, response.statusText);
      const responseData = await response.json();
      console.log('Данные ответа:', responseData);

      if (!response.ok) {
        throw new Error(responseData.error || `Ошибка при запросе к серверу (статус: ${response.status})`);
      }

      const { data } = responseData;    
      if (!data) {
        throw new Error('Не найдено подходящего маршрута');
      }

      console.log('Полученные данные optimalRoute:', data);
      
      // Дополнительное логирование для понимания структуры данных о производстве
      if (data && data.products) {
        console.log('Продукты в оптимальном маршруте:', data.products);
        data.products.forEach((product, index) => {
          console.log(`Продукт ${index + 1}:`, {
            name: product.name,
            supplier: product.supplier,
            supplierInfo: product.supplierInfo,
            allKeys: Object.keys(product)
          });
        });
      }

      setOptimalRoute(data);
      if (onOptimalRouteChange) onOptimalRouteChange(data);
    } catch (err) {
      console.error('Ошибка в fetchOptimalRoute:', err);
      setError(err.message);
      setOptimalRoute(null);
      if (onOptimalRouteChange) onOptimalRouteChange(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectOffer = () => {
    if (!optimalRoute) return;
    console.log("AAA", {optimalRoute});

    console.log(optimalRoute.products)

    const materials = optimalRoute.products.map((item) => {
      // Строки товаров могли уже измениться к моменту пересборки — тогда категории
      // для позиции маршрута просто нет. Раньше сюда приходили только по клику
      // менеджера, и обращение к полю у ненайденного товара падало.
      const category = products.find((product_item) => product_item.name === item.name)?.product_group ?? null
      console.log(category)
      console.log(products)
      return {
        name: item.name,
        category: category,
        calculator: category, // Добавляем поле calculator для корректной работы handleDeliveryMethodChange
        pricePerOne: item.price || 0,
        quantity: item.quantity || 1,
        sum: (item.price || 0) * (item.quantity || 1),
        date: new Date().toLocaleDateString('ru-RU'),
        unit: 'шт',
        bitrixId: item.bitrixId || "",
        production: item.supplier || 'Не определён',
        source: item.source || '-',
      }});

    const deliveryData = {
      name: optimalRoute.deliveryVehicle || "Не указано",
      numberOfTrips: optimalRoute.deliveryNumberOfTrips || 0,
      costPerTrip: optimalRoute.deliveryCostPerTrip || 0,
      totalCost: optimalRoute.deliveryTotalCost || 0,
      bitrixId: optimalRoute.deliveryBitrixId || "",
    };

    appliedOptimalRouteRef.current = optimalRoute;

    setSelectedOffer({
      production: "Оптимальный маршрут",
      materials: materials,
      delivery: deliveryData,
      deliveryMethods: optimalRoute.deliveryMethods || [], // Добавляем deliveryMethods
      date: materials[0]?.date || "",
    });

    console.log('Выбранное предложение отправлено в App.js для прокрутки:', {
      production: optimalRoute.manufacturers[0],
      materials,
      delivery: deliveryData,
    });
  };

  // Клик менеджера: КП стоит на странице выше маршрута, и без прокрутки выбор
  // выглядел так, будто кнопка не работает. Прокрутка — как у «Выбрать» в
  // сравнении производств; при автоматической пересборке ниже страница не двигается.
  const handleSelectClick = () => {
    if (!optimalRoute) return;
    handleSelectOffer();
    setTimeout(() => {
      const target = offerRef?.current || document.querySelector('.rd-kp-section');
      if (target) {
        scrollAppIntoView(target);
      }
    }, 300);
  };

  // Расчёт заказа, собранный из оптимального маршрута, пересобирается вместе с самим
  // маршрутом. Раньше он писался только по клику на «Выбрать»: маршрут наверху
  // пересчитывался на смену количества, номенклатуры и формы оплаты (см. зависимости
  // эффекта выше), а КП внизу оставалось от прежних данных — и никакого способа
  // заметить это у менеджера не было.
  //
  // Ссылка на уже применённый маршрут держит эффект от повторного срабатывания:
  // setSelectedOffer вызывает новый рендер, а пересобирать нужно только на новых
  // данных. Её же выставляет handleSelectOffer, поэтому клик не приводит к
  // повторному применению того же маршрута.
  useEffect(() => {
    if (!optimalRoute) return;
    if (selectedOffer?.production !== "Оптимальный маршрут") return;
    if (appliedOptimalRouteRef.current === optimalRoute) return;

    handleSelectOffer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optimalRoute, selectedOffer]);

  if (selfPickUp) {
    return null;
  }

  return (
    optimalRoute ? (
      <div className="rd-optimal">
        <div className="rd-optimal__head">
          <h3 className="rd-optimal__title">Оптимальный маршрут</h3>
        </div>

        {error && <div className="rd-optimal__alert rd-optimal__alert--error">{error}</div>}
        {loading && <div className="rd-optimal__alert rd-optimal__alert--info">Расчёт маршрута…</div>}

        <div className="rd-optimal__info">
          {optimalRoute.manufacturers.map((m, i) => (
            <div className="rd-optimal__info-row" key={i}>
              <span>{m}</span>
              <span className="rd-optimal__pill rd-optimal__pill--ok">Доступно</span>
            </div>
          ))}
          <div className="rd-optimal__info-row">
            <span>Клиент ({addressAndCoordinates.address})</span>
            <span className="rd-optimal__pill rd-optimal__pill--dest">Точка назначения</span>
          </div>
        </div>

        <div className="rd-optimal__body" ref={tableContainerRef} style={{ position: 'relative' }}>
          <table className="rd-kp-table">
            <thead>
              <tr>
                <th>№</th>
                <th className="rd-kp-th--text">Наименование</th>
                <th>Цена за ед.</th>
                <th>Количество</th>
                <th>Сумма</th>
                <th className="rd-kp-th--text">Поставщик</th>
                <th>Сколько дней цене</th>
                <th className="rd-kp-th--text">Источник</th>
              </tr>
            </thead>
            <tbody>
              {optimalRoute.products.map((item, index) => (
                <tr key={index}>
                  <td className="text-center">{index + 1}</td>
                  <td className="rd-kp-td--text">{item.name}</td>
                  <td className="text-center">{(item.price || 0).toLocaleString('ru-RU')} руб.</td>
                  <td className="text-center">{item.quantity || 1}</td>
                  <td className="text-center">{((item.price || 0) * (item.quantity || 1)).toLocaleString('ru-RU')} руб.</td>
                  <td
                    className="rd-kp-td--text rd-optimal__supplier"
                    onMouseEnter={(e) => handleMouseEnter(e, item.supplierInfo || item.supplier || 'Информация о поставщике недоступна')}
                    onMouseLeave={handleMouseLeave}
                  >
                    {item.supplier || 'Не определён'}
                  </td>
                  <td className="text-center">
                    {item.date ? (
                      (() => {
                        const daysCount = Math.max(1,
                          Math.floor(
                            (new Date() - new Date(item.date.split('.').reverse().join('-'))) /
                            (1000 * 60 * 60 * 24)
                          )
                        );
                        const isOldPrice = daysCount > 14;
                        return (
                          <span className={isOldPrice ? 'rd-optimal__old-price' : undefined}>{daysCount}</span>
                        );
                      })()
                    ) : "—"}
                  </td>
                  <td className="rd-kp-td--text">{item.source || '-'}</td>
                </tr>
              ))}
              <tr key={optimalRoute.deliveryCostPerTrip}>
                <td className="text-center">{optimalRoute.products.length + 1}</td>
                <td className="rd-kp-td--text">{optimalRoute.deliveryVehicle}</td>
                <td className="text-center">{(Math.ceil(optimalRoute.deliveryCostPerTrip) || 0).toLocaleString('ru-RU')} руб.</td>
                <td className="text-center">{optimalRoute.deliveryNumberOfTrips || 0}</td>
                <td className="text-center">{(Math.ceil(optimalRoute.deliveryTotalCost) || 0).toLocaleString('ru-RU')} руб.</td>
                <td className="text-center">Доставка</td>
                <td className="text-center"></td>
                <td className="text-center"></td>
              </tr>
              <tr className="rd-kp-subtotal">
                <td colSpan="4" className="rd-kp-subtotal__label">Итого:</td>
                <td className="text-center">{(Math.ceil(optimalRoute.totalCost) || 0).toLocaleString('ru-RU')} руб.</td>
                <td></td>
                <td></td>
                <td></td>
              </tr>
            </tbody>
          </table>

          {tooltip.visible && tooltip.text && (
            <div
              ref={tooltipRef}
              className="rd-optimal__tooltip"
              style={{ left: `${tooltip.x}px`, top: `${tooltip.y}px` }}
              dangerouslySetInnerHTML={{ __html: tooltip.text?.replace(/\n/g, '<br/>') || tooltip.text }}
            />
          )}
        </div>

        <div className="rd-optimal__footer">
          <button className="rd-optimal__select" onClick={handleSelectClick}>Выбрать</button>
          <small className="rd-optimal__distance">Общее расстояние: <b>{optimalRoute.totalDistance} км</b></small>
        </div>
      </div>
    ) : null
  );
}

export default Optimal;